/**
 * Collapse — animates a section open/closed by its measured height, with a
 * fade. Content stays mounted while closing so the motion is visible; after
 * closing it unmounts (cheap for long lists). Starts at its final state on
 * first render (no animation on page load).
 *
 *   <Collapse open={showAltLines}>...</Collapse>
 *   <Collapse open={showMath}>{() => <ExpensiveMath />}</Collapse>   // built only while shown
 */
import React, { useEffect, useRef, useState } from 'react';
import { Animated, View } from 'react-native';
import { timing, prefersReducedMotion } from '../../utils/motion';

export default function Collapse({ open, children, style, keepMounted = false }) {
    const [mounted, setMounted] = useState(open);
    const [height, setHeight] = useState(null);
    const h = useRef(new Animated.Value(open ? 1 : 0)).current;
    const first = useRef(true);
    const openAtMount = useRef(open).current;

    useEffect(() => {
        if (first.current) { first.current = false; return undefined; }
        if (open) setMounted(true);
        const anim = timing(h, open ? 1 : 0, { duration: open ? 'slow' : 'base', easing: open ? 'out' : 'in', useNative: false });
        anim.start(({ finished }) => { if (finished && !open && !keepMounted) setMounted(false); });
        return () => anim.stop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!mounted && !keepMounted) return null;
    const reduced = prefersReducedMotion();
    const measured = height !== null;
    // First open: measure the content off-flow (absolute) while the frame is
    // still 0 tall, then animate the frame to that height.
    // A section that starts open renders naturally (no 0-height first frame).
    const measuring = !measured && !reduced && !openAtMount;
    return (
        <Animated.View
            style={[
                { overflow: 'hidden' },
                !reduced && measured && { height: h.interpolate({ inputRange: [0, 1], outputRange: [0, height] }), opacity: h },
                measuring && { height: 0, opacity: 0 },
                reduced && !open && { height: 0, opacity: 0 },
                style,
            ]}
            aria-hidden={!open}
        >
            <View
                style={measuring ? { position: 'absolute', left: 0, right: 0, top: 0 } : null}
                onLayout={(e) => { const nh = e.nativeEvent.layout.height; if (nh > 0 && nh !== height) setHeight(nh); }}
            >
                {typeof children === 'function' ? children() : children}
            </View>
        </Animated.View>
    );
}
